import styled from "styled-components";
import { useState } from "react";
import { Modal, Skeleton, Grid, Row, Divider } from "antd";
import { IconCloseSvg } from "@/components/svgs";
import { FormAirFreightInfo, FormAirFreightSummary } from "@/components/organisms";
import { NOTIFICATION_TYPE } from "@/config/constants";
import { initFormAirFreight } from "@/utils/initializing";
import { toast } from "@/modules/toast";
import { ModalStatus } from "../ModalStatus";
import { isEmpty } from "lodash";
import { RAButtonPrimary } from "@/components/customs";
import { useSelector } from "react-redux";
import { selectAuthState } from "@/stores/slices/authSlice";
import { createAirFreightRequest, createAirFreightRequestPublic } from "@/stores/apis/apiAirFreight";
import { HeaderSection } from "../../Cards";
import { REGEX_EMAIL } from "@/config/constants";

const { useBreakpoint } = Grid;

const AirFreightRequestStep = {
	AIR_FREIGHT_INFO: 0,
	AIR_FREIGHT_SUMMARY: 1,
};

export function ModalRequestAirFreight({ visible = false, onClose }) {
	const { user } = useSelector(selectAuthState);
	const screens = useBreakpoint();
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState(AirFreightRequestStep.AIR_FREIGHT_INFO);
	const [formRequest, setFormRequest] = useState(initFormAirFreight);
	const [showStatus, setShowStatus] = useState(false);
	const [errors, setErrors] = useState({});

	const onNext = () => {
		setStep(step + 1);
	};

	const onBack = () => {
		setStep(step - 1);
	};

	const validationForm = () => {
		let valid = true;

		let errs = {};
		if (!formRequest.from_airport) {
			errs.from_airport = "Please select departure airport";
		}
		if (!formRequest.to_airport) {
			errs.to_airport = "Please select arrival airport";
		}
		if (formRequest.from_airport && formRequest.from_airport?._id === formRequest.to_airport?._id) {
			errs.from_airport = "Departure airport and arrival airport should be different";
		}
		if (!formRequest.company_name) {
			errs.company_name = "Please enter company/name";
		}
		if (!formRequest.contact?.name) {
			errs.contact = {
				...errs.contact,
				name: "Please enter contact name",
			};
		}
		if (!formRequest.contact?.email) {
			errs.contact = {
				...errs.contact,
				email: "Please enter contact email",
			};
		} else if (!REGEX_EMAIL.test(formRequest.contact.email.trim())) {
			errs.contact = {
				...errs.contact,
				email: "Please enter a valid email address.",
			};
		}
		if (!formRequest.contact?.phone) {
			errs.contact = {
				...errs.contact,
				phone: "Please enter contact phone",
			};
		}
		if (!formRequest.cargo_info?.quantity) {
			errs.cargo_info = {
				...errs.cargo_info,
				quantity: "Please enter cargo quantity",
			};
		}
		if (!formRequest.cargo_info?.weight_lb) {
			errs.cargo_info = {
				...errs.cargo_info,
				weight_lb: "Please enter weight lb",
			};
		}
		if (!formRequest.cargo_info?.heigh_inch) {
			errs.cargo_info = {
				...errs.cargo_info,
				heigh_inch: "Please enter height (inches)",
			};
		}
		if (!formRequest.cargo_info?.width_inch) {
			errs.cargo_info = {
				...errs.cargo_info,
				width_inch: "Please enter width (inches)",
			};
		}
		if (!formRequest.cargo_info?.length_inch) {
			errs.cargo_info = {
				...errs.cargo_info,
				length_inch: "Please enter length (inches)",
			};
		}

		if (!isEmpty(errs)) {
			valid = false;
			setErrors(errs);
		}

		return valid;
	};

	const onSubmitRequest = async () => {
		if (loading) return;
		setLoading(true);

		const payload = {
			from_airport: formRequest?.from_airport?._id,
			to_airport: formRequest?.to_airport?._id,
			note: formRequest?.note,
			company_name: formRequest?.company_name,
			contact: {
				name: formRequest.contact.name,
				email: formRequest.contact.email,
				phone: formRequest.contact.phone,
			},
			cargo_info: {
				quantity: formRequest.cargo_info.quantity,
				weight_lb: formRequest.cargo_info.weight_lb,
				heigh_inch: formRequest.cargo_info.heigh_inch,
				width_inch: formRequest.cargo_info.width_inch,
				length_inch: formRequest.cargo_info.length_inch,
			},
		};

		const fetchAPI = !user?._id ? createAirFreightRequestPublic : createAirFreightRequest;
		let result = await fetchAPI(payload);
		if (result && result.success) {
			setShowStatus(true);
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: "Air Freight Request Failed.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 200);
	};

	const _renderConentByType = () => {
		switch (step) {
			case AirFreightRequestStep.AIR_FREIGHT_INFO:
				return (
					<>
						<HeaderSection
							title="Request Air Freight Quote"
							description="Welcome to our air freight request page. Kindly submit your details, and we'll promptly provide a personalized quote for your time-sensitive shipments."
						/>
						<Divider />
						<FormAirFreightInfo
							data={formRequest}
							error={errors}
							onChange={(values) => {
								setFormRequest({ ...values });
								if (!isEmpty(errors)) {
									setErrors({});
								}
							}}
							loading={loading}
						/>
						<Row justify="center" align="middle" style={{ marginTop: "40px" }}>
							<RAButtonPrimary
								data-testid="airFreightInfo_nextBtn"
								title="NEXT"
								onClick={() => {
									const valid = validationForm();
									if (valid) {
										onNext();
									}
								}}
								width="160px"
							/>
						</Row>
					</>
				);
			case AirFreightRequestStep.AIR_FREIGHT_SUMMARY:
				return (
					<FormAirFreightSummary
						data={formRequest}
						onBack={onBack}
						onSubmitRequest={onSubmitRequest}
						loading={loading}
					/>
				);
			default:
				return <Skeleton style={{ margin: "0 12px" }} />;
		}
	};

	const getWidthModal = () => {
		let width = "auto";
		if (screens.md || screens.lg || screens.xl || screens.xxl) {
			width = "720px";
		}
		return width;
	};

	return (
		<Modal
			centered
			closeIcon={<IconCloseSvg />}
			open={visible}
			onCancel={() => {
				setStep(AirFreightRequestStep.AIR_FREIGHT_INFO);
				setFormRequest(initFormAirFreight);
				onClose(false);
			}}
			footer={null}
			width={getWidthModal()}
			wrapClassName="auth-wrapper"
			style={{ padding: 0 }}
			maskClosable={false}
		>
			<Container>
				{_renderConentByType()}
				<ModalStatus
					visible={showStatus}
					status={NOTIFICATION_TYPE.SUCCESS}
					title="Air Freight Request Sent Successfully!"
					note="Your request has been sent successfully. Our support team will contact you shortly."
					onClose={() => {
						setShowStatus(false);
						setTimeout(() => {
							setStep(AirFreightRequestStep.AIR_FREIGHT_INFO);
							setFormRequest(initFormAirFreight);
							onClose();
						}, 300);
					}}
				/>
			</Container>
		</Modal>
	);
}

const Container = styled.div`
	padding-top: 30px;
	padding-bottom: 30px;
	max-width: 720px;
	min-width: 343px;
	padding: clamp(24px, 8vw, 60px) clamp(16px, 4vw, 60px);
`;
